import { DOCUMENT } from '@angular/common';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { clearLegacyOwnedLocalStorage } from './auth.constants';
import { isMobileAuthContext } from './auth-device.util';

export type LogoutReason = 'manual' | 'token_expired' | 'inactive';

const LOGOUT_REASON_KEY = 'logout_reason';
export const LOGOUT_RETURN_URL_KEY = 'logout_return_url';
export const LOGOUT_IN_PROGRESS_KEY = '__logout_in_progress__';
export const LOGOUT_NAVIGATE_TO_LOGIN = new InjectionToken<() => void>(
  'LOGOUT_NAVIGATE_TO_LOGIN',
  {
    providedIn: 'root',
    factory: () => {
      const document = inject(DOCUMENT);
      return () => {
        document.location.href = '/login';
      };
    }
  }
);

@Injectable({
  providedIn: 'root'
})
export class LogoutService {
  readonly #msalService = inject(MsalService);
  readonly #navigateToLogin = inject(LOGOUT_NAVIGATE_TO_LOGIN);

  /** Store the latest logout reason for the next login screen to consume */
  setLogoutReason(reason: LogoutReason): void {
    sessionStorage.setItem(LOGOUT_REASON_KEY, reason);
  }

  /** Read the last logout reason without clearing it */
  getLogoutReason(): LogoutReason | null {
    return (sessionStorage.getItem(LOGOUT_REASON_KEY) as LogoutReason | null) ?? null;
  }

  /** Read and clear the last logout reason */
  consumeLogoutReason(): LogoutReason | null {
    const reason = this.getLogoutReason();
    sessionStorage.removeItem(LOGOUT_REASON_KEY);
    return reason;
  }

  /** Clear any stored logout reason */
  clearLogoutReason(): void {
    sessionStorage.removeItem(LOGOUT_REASON_KEY);
  }

  /** Store a safe internal return target, skipping routes that render login. */
  setReturnUrl(returnUrl: string): void {
    const target = sanitizeReturnUrl(returnUrl);
    if (target === null) {
      return;
    }

    sessionStorage.setItem(LOGOUT_RETURN_URL_KEY, target);
  }

  /** Read the saved return URL without clearing it */
  getReturnUrl(): string | null {
    const storedTarget = sessionStorage.getItem(LOGOUT_RETURN_URL_KEY);
    const target = sanitizeReturnUrl(storedTarget);
    if (storedTarget !== null && target === null) {
      this.clearReturnUrl();
    }
    return target;
  }

  /** Remove any saved return URL */
  clearReturnUrl(): void {
    sessionStorage.removeItem(LOGOUT_RETURN_URL_KEY);
  }

  /** Read and clear the saved return URL */
  consumeReturnUrl(): string | null {
    const returnUrl = this.getReturnUrl();
    this.clearReturnUrl();
    return returnUrl;
  }

  /** Clear the re-entrancy guard once logout is finished */
  clearLogoutInProgress(): void {
    sessionStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
  }

  /** Redirect the user to the login page and remember why the session ended */
  logout(reason: LogoutReason = 'manual'): void {
    if (!this.beginLogout(reason)) {
      return;
    }

    this.completeLogout();
  }

  /** Set the synchronous re-entry guard before any logout side effects run. */
  beginLogout(reason: LogoutReason): boolean {
    if (sessionStorage.getItem(LOGOUT_IN_PROGRESS_KEY)) {
      return false;
    }

    sessionStorage.setItem(LOGOUT_IN_PROGRESS_KEY, 'true');
    this.setLogoutReason(reason);

    return true;
  }

  /** Clear portal and MSAL state, then always load the login route. */
  completeLogout(): void {
    clearLegacyOwnedLocalStorage();
    void this.#clearMsalStateAndRedirect();
  }

  async #clearMsalStateAndRedirect(): Promise<void> {
    try {
      if (isMobileAuthContext()) {
        await this.#msalService.instance.logoutRedirect({
          postLogoutRedirectUri: `${window.location.origin}/home`
        });
        return;
      }

      await this.#msalService.instance.logoutRedirect({
        onRedirectNavigate: () => false
      });
    } catch (error) {
      console.warn('Could not clear the MSAL cache during logout', error);
    } finally {
      try {
        this.#msalService.instance.setActiveAccount(null);
      } catch (error) {
        console.warn('Could not clear the active MSAL account during logout', error);
      }

      if (!isMobileAuthContext()) {
        this.#navigateToLogin();
      }
    }
  }
}

function sanitizeReturnUrl(value: string | null): string | null {
  if (!value || value !== value.trim() || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) {
    return null;
  }

  try {
    const target = new URL(value, 'https://portal.local');
    if (target.origin !== 'https://portal.local') {
      return null;
    }

    const decodedPath = decodeURIComponent(target.pathname);
    const segments = decodedPath.split('/');
    if (segments.some((segment) => segment === '.' || segment === '..')) {
      return null;
    }
    if (decodedPath === '/' || decodedPath === '/login' || decodedPath.startsWith('/login/')) {
      return null;
    }

    return `${target.pathname}${target.search}${target.hash}`;
  } catch {
    return null;
  }
}
